import { Customer } from "../models/User";
import { WorkspaceSummary } from "../models/Workspace";

export interface WorkspaceSessionUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface WorkspaceSession {
  activeWorkspaceId: string | null;
  workspaces: WorkspaceSummary[];
  customer: Customer | null;
  workspaceContextEnabled: boolean;
  workspaceFeaturesEnabled: boolean;
}

function legacyWorkspaceName(
  user: WorkspaceSessionUser,
  customer: Customer,
): string {
  const label =
    customer.name?.trim() ||
    user.displayName?.trim() ||
    user.email?.split("@")[0]?.trim();
  return label ? `${label}'s workspace` : "My workspace";
}

export function normalizeWorkspaceSession(
  payload: any,
  user: WorkspaceSessionUser,
): WorkspaceSession {
  if (Array.isArray(payload?.workspaces)) {
    return {
      activeWorkspaceId: payload.activeWorkspaceId || null,
      workspaces: payload.workspaces,
      customer: payload.customer || null,
      // A workspace-aware server may hide management entry points while it
      // continues routing requests for already-created opaque workspaces.
      // Fall back to the old combined flag during a rolling deployment.
      workspaceContextEnabled:
        payload.workspaceContextEnabled === true ||
        payload.workspaceFeaturesEnabled === true,
      workspaceFeaturesEnabled: payload.workspaceFeaturesEnabled === true,
    };
  }

  // Temporary compatibility with the pre-workspace ensure response. This
  // keeps a new client usable while the workspace-aware server rolls out.
  const customer = payload?.customer as Customer | undefined;
  if (!customer) throw new Error("The account response is incomplete.");
  const workspaceId = customer.workspaceId || customer.uid || user.uid;
  const joinedAt =
    typeof customer.createdAt === "number" ? customer.createdAt : Date.now();
  return {
    activeWorkspaceId: workspaceId,
    workspaces: [
      {
        id: workspaceId,
        name: legacyWorkspaceName(user, customer),
        ownerId: user.uid,
        role: "owner",
        status: "active",
        joinedAt,
        setupPillDismissed: customer.metadata?.setupPillDismissed === true,
      },
    ],
    customer,
    workspaceContextEnabled: false,
    workspaceFeaturesEnabled: false,
  };
}
